import { Page } from "@/app/shared/components/page";
import { Switch } from "@/app/shared/ui/switch";
import { useDemoModeStore } from "@/store/demoMode";
import { AppSettings } from "@groot/shell/components/AppSettings";
import { PasskeyManager } from "@groot/shell/components/PasskeyManager";
import { PageHeader } from "@groot/shell/components/layout";
import { Label } from "@groot/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@groot/ui/tabs";
import {
  ArrowDownUp,
  ArrowLeftRight,
  Cable,
  Database,
  Eye,
  FolderTree,
  HeartPulse,
  Settings as SettingsIcon,
  ShieldCheck,
} from "lucide-react";
import { BankSyncIntegrations } from "./components/BankSyncIntegrations";
import { BeancountSyncSettings } from "./components/BeancountSyncSettings";
import { CategoryManagement } from "./components/CategoryManagement";
import { TransferReviewQueue } from "./components/TransferReviewQueue";
import { DataHealthPanel } from "./components/DataHealthPanel";

export function Settings() {
  const { enabled: isDemoMode, toggleEnabled } = useDemoModeStore();

  return (
    <Page maxWidth="6xl">
      <PageHeader
        title="Settings"
        description="Manage your application preferences, configuration, and security."
      />
      <div className="mt-8">
        <Tabs defaultValue="general">
          <TabsList variant="line">
            <TabsTrigger value="general">
              <SettingsIcon className="size-4" />
              General
            </TabsTrigger>
            <TabsTrigger value="categories">
              <FolderTree className="size-4" />
              Categories
            </TabsTrigger>
            <TabsTrigger value="integrations">
              <Cable className="size-4" />
              Integrations
            </TabsTrigger>
            <TabsTrigger value="transfers">
              <ArrowLeftRight className="size-4" />
              Transfers
            </TabsTrigger>
            <TabsTrigger value="data-health">
              <HeartPulse className="size-4" />
              Data Health
            </TabsTrigger>
            <TabsTrigger value="beancount">
              <ArrowDownUp className="size-4" />
              Beancount
            </TabsTrigger>
            <TabsTrigger value="app">
              <Database className="size-4" />
              App Config
            </TabsTrigger>
            <TabsTrigger value="security">
              <ShieldCheck className="size-4" />
              Security
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="mt-6">
            <div className="rounded-lg border border-border p-4">
              <div className="flex items-center justify-between space-x-4">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <Eye className="size-4 text-muted-foreground" />
                    <Label htmlFor="demo-mode" className="text-base font-medium cursor-pointer">
                      Demo Mode
                    </Label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Show randomized amounts instead of actual financial data. Useful for demos and
                    screenshots.
                  </p>
                </div>
                <Switch id="demo-mode" checked={isDemoMode} onCheckedChange={toggleEnabled} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="categories" className="mt-6">
            <CategoryManagement />
          </TabsContent>

          <TabsContent value="integrations" className="mt-6">
            <BankSyncIntegrations />
          </TabsContent>

          <TabsContent value="transfers" className="mt-6">
            <TransferReviewQueue />
          </TabsContent>

          <TabsContent value="data-health" className="mt-6">
            <DataHealthPanel />
          </TabsContent>

          <TabsContent value="beancount" className="mt-6">
            <BeancountSyncSettings />
          </TabsContent>

          <TabsContent value="app" className="mt-6">
            <AppSettings />
          </TabsContent>

          <TabsContent value="security" className="mt-6">
            <PasskeyManager />
          </TabsContent>
        </Tabs>
      </div>
    </Page>
  );
}
